import moment from 'moment';
import { useCallback, useEffect, useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { AvailabilitySlot } from 'shared-types/Common';
import type { CalendarView, DayAvailability, TimeslotOption } from 'voyager';

type TimeslotAvailabilityParams = {
  rawTimeslots: AvailabilitySlot[] | undefined;
  selectedDateString: string;
  weekStartDate: string;
  debouncedWeekStartDate: string;
  isLoadingTimeslots: boolean;
  isTimeslotsError: boolean;
  form: UseFormReturn<any>;
  setWeekStartDate: (date: string) => void;
  calendarView: CalendarView;
  setCalendarView: (view: CalendarView) => void;
  guestCount?: number;
};

function groupSlotsByDate(slots: AvailabilitySlot[]): Record<string, AvailabilitySlot[]> {
  const grouped: Record<string, AvailabilitySlot[]> = {};
  slots.forEach((slot) => {
    const date = moment(slot.startDateTime).format('YYYY-MM-DD');
    if (!grouped[date]) grouped[date] = [];
    grouped[date].push(slot);
  });
  return grouped;
}

function slotSeats(slot: AvailabilitySlot, guestCount?: number): boolean {
  if (typeof guestCount !== 'number' || guestCount <= 0) return true;
  if (typeof slot.numberAvailable !== 'number') return true;
  return slot.numberAvailable >= guestCount;
}

export function useTimeslotAvailability({
  rawTimeslots,
  selectedDateString,
  weekStartDate,
  debouncedWeekStartDate,
  isLoadingTimeslots,
  isTimeslotsError,
  form,
  setWeekStartDate,
  calendarView,
  setCalendarView,
  guestCount,
}: TimeslotAvailabilityParams) {
  const slotsByDate = useMemo(() => {
    if (!rawTimeslots || !Array.isArray(rawTimeslots)) return {};
    return groupSlotsByDate(rawTimeslots);
  }, [rawTimeslots]);

  const availability: DayAvailability[] = useMemo(() => {
    const days: DayAvailability[] = [];
    for (let i = 0; i < 7; i++) {
      const date = moment(weekStartDate).add(i, 'days').format('YYYY-MM-DD');
      const daySlots = slotsByDate[date];
      days.push({
        date,
        available: !!(daySlots && daySlots.some((slot) => slotSeats(slot, guestCount))),
      });
    }
    return days;
  }, [slotsByDate, weekStartDate, guestCount]);

  useEffect(() => {
    if (!rawTimeslots) return;

    const currentDate = form.getValues('date');
    const currentIsAvailable = availability.find((d) => d.date === currentDate && d.available);
    if (currentIsAvailable) return;

    const firstAvailable = availability.find((d) => d.available);
    if (firstAvailable) {
      form.setValue('date', firstAvailable.date, { shouldValidate: true });
      form.setValue('arrivalTime', undefined as any, { shouldValidate: true });
    }
  }, [availability, rawTimeslots, form]);

  const timeslotOptions: TimeslotOption[] = useMemo(() => {
    const daySlots = slotsByDate[selectedDateString];
    if (!daySlots || daySlots.length === 0) return [];
    return daySlots.map((slot) => {
      const start = moment(slot.startDateTime).format('HH:mm');
      const end = moment(slot.endDateTime).format('HH:mm');
      return {
        value: start,
        label: `${start} - ${end}`,
      };
    });
  }, [slotsByDate, selectedDateString]);

  const handleDateSelect = useCallback(
    (date: string) => {
      if (calendarView === 'month') {
        const weekStart = moment(date).startOf('isoWeek').format('YYYY-MM-DD');
        setWeekStartDate(weekStart);
        setCalendarView('week');
      }
      form.setValue('date', date, { shouldValidate: true });
      form.setValue('arrivalTime', undefined as any, { shouldValidate: true });
    },
    [form, calendarView, setCalendarView, setWeekStartDate]
  );

  const isWeekViewStable =
    calendarView === 'week' && weekStartDate === debouncedWeekStartDate && !isLoadingTimeslots;

  const hasNoAvailabilityAllWeek =
    isWeekViewStable &&
    (isTimeslotsError ||
      (Array.isArray(rawTimeslots) && !availability.some((d) => d.available)));

  const hasNoAvailabilityToday =
    hasNoAvailabilityAllWeek ||
    (isWeekViewStable &&
      !slotsByDate[selectedDateString]?.some((slot) => slotSeats(slot, guestCount)) &&
      !availability.some((d) => d.available));

  return {
    slotsByDate,
    availability,
    timeslotOptions,
    handleDateSelect,
    hasNoAvailabilityAllWeek,
    hasNoAvailabilityToday,
  };
}
